<script setup>
import { ref, computed } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import api from 'dashboard/api/aceleres/iaRodizios';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  material: { type: Object, default: null },
  defaultSection: { type: String, default: 'zoraxy' },
  teamId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close', 'saved']);

const isEdit = computed(() => !!props.material?.id);

const form = ref({
  title: props.material?.title || '',
  content: props.material?.content || '',
  priority: props.material?.priority ?? 0,
  is_active: props.material?.is_active ?? true,
  source_type: props.material?.source_type || 'text',
  section: props.material?.section || props.defaultSection || 'zoraxy',
  file: null,
});

const saving = ref(false);

const onFileChange = e => {
  const f = e.target.files?.[0];
  if (f) {
    form.value.file = f;
    form.value.source_type = 'file';
  }
};

const submit = async () => {
  if (!form.value.title.trim()) {
    useAlert('Informe o título do material');
    return;
  }
  saving.value = true;
  try {
    if (isEdit.value) {
      await api.updateMaterial(props.material.id, form.value, props.teamId);
    } else {
      await api.createMaterial(form.value, props.teamId);
    }
    useAlert(isEdit.value ? 'Material atualizado' : 'Material criado');
    emit('saved');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao salvar material');
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <Modal :show="true" :on-close="() => emit('close')" size="medium">
    <div class="p-6 flex flex-col gap-4 w-full">
      <h2 class="text-display-small text-n-slate-12 font-medium m-0">
        {{ isEdit ? 'Editar material' : 'Novo material' }}
      </h2>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Seção *</label>
        <select v-model="form.section"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12">
          <option value="zoraxy">Atendimento — Zoraxy</option>
          <option value="logos">Vendedores — LOGOS</option>
        </select>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Título *</label>
        <input v-model="form.title" type="text" maxlength="200"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
      </div>

      <div class="flex gap-4">
        <div class="flex flex-col gap-1.5 w-32">
          <label class="text-body-small text-n-slate-12 font-medium">Prioridade</label>
          <input v-model.number="form.priority" type="number" min="0" max="1000"
            class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
        </div>
        <div class="flex items-end pb-2">
          <label class="flex items-center gap-2 text-body-small text-n-slate-12">
            <input v-model="form.is_active" type="checkbox" />
            Ativo
          </label>
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Conteúdo (texto)</label>
        <textarea v-model="form.content" rows="6"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12"
          placeholder="Cole aqui o conteúdo textual ou deixe vazio se for usar um arquivo." />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Arquivo (opcional)</label>
        <input type="file" accept=".pdf,.txt,.docx,.xlsx,.csv,.md" @change="onFileChange" />
        <span v-if="props.material?.file_name && !form.file" class="text-body-small text-n-slate-11">
          Arquivo atual: {{ props.material.file_name }}
        </span>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <Button label="Cancelar" variant="ghost" @click="emit('close')" />
        <Button :label="isEdit ? 'Salvar alterações' : 'Criar material'"
                :is-loading="saving" @click="submit" />
      </div>
    </div>
  </Modal>
</template>
